import React, { useContext, useState, KeyboardEvent } from 'react'
import * as yup from 'yup'
import { GridItem } from '@zuto/core/src/grid'
import { DateOfBirth, DateOfBirthValues } from '@zuto/molecules'
import { Input } from '@zuto/atoms'
import { useCallbackOnce } from '@zuto/utilities'
import Box from '../../../../packages/core/src/box'
import QuestionComponentProps from '../../../Questions/question-component-props'
import PersonalDetailsValidator from '../../../validators/PersonalDetailsValidator'
import QuestionKey from '../../../Questions/question-key'
import { ApplicationFormContext } from '../../ApplicationForm/ApplicationFormContext'
import AppFormQuestion from '../../AppFormQuestion'
import withQuestion from '../../HOC/withQuestion'
import withFade from '../../HOC/withFade'
import PersonalDetailsAnswers from './PersonalDetailsAnswers'
import { fireReactGAEvent, handlePrevButtonClick } from '../utils'
import NavigationButtons from '../../NavigationButtons/NavigationButtons'
import { AttributionContext } from '../../../context'

const PersonalDetailsQuestion = ({
  existingAnswers,
  disablePrevious
}: QuestionComponentProps): JSX.Element => {
  const appFormContext = useContext(ApplicationFormContext)
  const attribution = useContext(AttributionContext)
  const [answer, setAnswer] = useState<PersonalDetailsAnswers>(existingAnswers)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [nextButtonCb, resetNextQuestionCb] = useCallbackOnce()
  const [previousButtonCb] = useCallbackOnce()

  const isAllFieldsFilled = answer?.dateOfBirth?.day
    && answer?.dateOfBirth?.month
    && answer?.dateOfBirth?.year
    && answer?.surname
    && answer?.firstName

  const personalDetailsValidator = PersonalDetailsValidator(attribution)
  const schema = personalDetailsValidator.getValidator()

  const valdiateInput = (): boolean => {
    try {
      const { dateOfBirth } = answer
      schema.validateSync({
        firstName: answer.firstName?.trim(),
        surname: answer.surname?.trim(),
        dateOfBirth: `${dateOfBirth?.day}-${dateOfBirth?.month}-${dateOfBirth?.year}`
      }, {
        abortEarly: false
      })
    } catch (e: unknown) {
      const err = e as yup.ValidationError
      const prettyErrors: Record<string, string> = {}
      if (!err.inner) return false

      err.inner.forEach((validationError, index) => {
        if (!validationError.path) return

        if (index === 0) {
          document.getElementById(validationError.path)?.scrollIntoView()
        }

        const [errorMessage] = validationError.errors
        prettyErrors[validationError.path.toString()] = errorMessage
        fireReactGAEvent({
          category: 'Application Form',
          action: 'Validation Error',
          label: validationError.path.toString(),
          nonInteraction: true
        })
      })
      setErrors(prettyErrors)
      return false
    }

    return true
  }

  const nextQuestion = () => {
    const valid = valdiateInput()
    if (!valid) {
      resetNextQuestionCb()
      return
    }
    const normalisedValues = personalDetailsValidator.getNormalisedValues?.({
      personalDetails: answer
    })
    appFormContext.dispatch({
      type: 'QUESTION_ANSWERED',
      name: QuestionKey.PERSONAL_DETAILS,
      value: {
        ...answer,
        firstName: normalisedValues?.firstName ?? answer.firstName,
        surname: normalisedValues?.surname ?? answer.surname
      }
    })
  }

  const previousQuestion = () => {
    handlePrevButtonClick(
      appFormContext.dispatch,
      { name: QuestionKey.PERSONAL_DETAILS, value: answer }
    )
  }

  const updateAnswer = (key: string, value: string | DateOfBirthValues) => {
    setAnswer((prev) => ({
      ...prev,
      [key]: value
    }))
  }

  const inputOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateAnswer(e.currentTarget.name, e.currentTarget.value)
  }

  const handleEnterKeyPress = (e: KeyboardEvent) => {
    if (e.code === 'Enter' && answer) {
      nextButtonCb(nextQuestion)
    }
  }

  return (
    <>
      <GridItem gridColumn={['auto / span 12', '3 / span 8']}>
        <AppFormQuestion
          mt={3}
          mb={2}
          questionKey={`${QuestionKey.PERSONAL_DETAILS}.name`}
          textStyle="material.body.medium.smallLineHeight"
        >
          What&apos;s your name?
        </AppFormQuestion>
        <Box mb={12}>
          <Input
            name="firstName"
            label="First name"
            labelVariant="experimental"
            data-testid="firstName"
            defaultValue={existingAnswers && existingAnswers.firstName}
            error={errors.firstName}
            onChange={inputOnChange}
            onKeyPress={handleEnterKeyPress}
            maxLength={50}
            data-hj-suppress=""
          />
        </Box>
        <Box mb={13}>
          <Input
            name="surname"
            label="Surname"
            labelVariant="experimental"
            data-testid="surname"
            defaultValue={existingAnswers && existingAnswers.surname}
            error={errors.surname}
            onChange={inputOnChange}
            onKeyPress={handleEnterKeyPress}
            maxLength={50}
            data-hj-suppress=""
          />
        </Box>
        <AppFormQuestion
          questionKey={`${QuestionKey.PERSONAL_DETAILS}.dateOfBirth`}
          mb={2}
          textStyle="material.body.medium.smallLineHeight"
        >
          What&apos;s your date of birth?
        </AppFormQuestion>
        <DateOfBirth
          defaultValue={existingAnswers && existingAnswers.dateOfBirth}
          mt={0}
          mb={14}
          labelVariant="experimental"
          valueChanged={(value) => updateAnswer('dateOfBirth', value)}
          onKeyPress={handleEnterKeyPress}
          error={errors.dateOfBirth}
          autoFocus={false}
        />
      </GridItem>
      <NavigationButtons
        nextHandler={() => nextButtonCb(nextQuestion)}
        previousHandler={() => previousButtonCb(previousQuestion)}
        disableNext={!isAllFieldsFilled}
        disablePrevious={disablePrevious ? 'invisible' : undefined}
      />
    </>
  )
}

export default withFade(withQuestion(PersonalDetailsQuestion))
